import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';

import { openWindow } from 'store/actions/windows';
import { getNodeTitle, getNodeTypeTitle } from 'services/getNodeTitle';
import getEquipmentInnerId from 'services/getEquipmentInnerId';
import { BellIcon, CaretIcon } from './icons';
import { Widget, WHead, WIco, WTitle, Chip, Caret, WBody, Row, Dot, RackId, Grow, Empty } from './widgets-dock.styled';

const SEV = { critical: 0, notification: 1, info: 2 };
const MUTED = ['Dispenser status', 'Stacker status'];

// сокращение типа: первые буквы слов в верхнем регистре («Въездная стойка» → «ВС»); слово, уже написанное капсом (АПП), остаётся как есть
const abbreviate = (text) =>
  text
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (w === w.toUpperCase() ? w : w[0].toUpperCase()))
    .join('');

export const AlarmsWidget = ({ open, onToggle }) => {
  const { t } = useTranslation();
  const events = useSelector((store) => store.events.events, shallowEqual);
  const dispatch = useDispatch();

  // flatten { source: { id: event } } → [event], drop noisy statuses, sort by severity
  const list = [];
  Object.keys(events || {}).forEach((source) => {
    Object.values(events[source] || {}).forEach((ev) => {
      if (ev.type === 'notification' && MUTED.indexOf(ev.message) !== -1) {
        return;
      }
      list.push(ev);
    });
  });
  list.sort((a, b) => (SEV[a.type] ?? 3) - (SEV[b.type] ?? 3));

  const critical = list.filter((e) => e.type === 'critical').length;
  const warnings = list.filter((e) => e.type === 'notification').length;

  // short — с сокращённым типом (для строки), иначе с полным (для tooltip)
  const getTitle = (source, short) => {
    const node = window.config.nodes ? window.config.nodes[source] : null;
    if (!node) {
      return getEquipmentInnerId(source);
    }
    const type = getNodeTypeTitle(node.type, node.id);
    return [short ? abbreviate(type) : type, node.caption].filter(Boolean).join(' ');
  };

  const goToRack = (source) => {
    const node = window.config.nodes ? window.config.nodes[source] : null;
    if (!node) {
      return;
    }
    dispatch(
      openWindow({
        id: node.id,
        type: 'rack',
        data: { id: node.id, type: node.type, barriersCount: node.barriersCount, withSignals: true },
        header: getNodeTitle(node.type, node.id, node.caption),
      })
    );
  };

  return (
    <Widget className={critical > 0 ? 'alert-red' : ''}>
      <WHead type="button" className={open ? '' : 'closed'} onClick={onToggle}>
        <WIco>
          <BellIcon />
        </WIco>
        <WTitle>{t('dock.Тревоги')}</WTitle>
        {critical > 0 && <Chip className="red">{critical}</Chip>}
        {warnings > 0 && <Chip className="amber">{warnings}</Chip>}
        {critical === 0 && warnings === 0 && <Chip className="ghost">0</Chip>}
        <Caret className={open ? 'open' : ''}>
          <CaretIcon />
        </Caret>
      </WHead>
      {open && (
        <WBody>
          {list.length === 0 && <Empty>{t('dock.Активных тревог нет')}</Empty>}
          {list.slice(0, 40).map((ev, i) => (
            <Row key={(ev.source || '') + '_' + (ev.id || i)} as="button" style={{ cursor: 'pointer', textAlign: 'left', width: '100%' }} onClick={() => goToRack(ev.source)}>
              <Dot className={ev.type === 'critical' ? 'red' : 'amber'} />
              <RackId title={getTitle(ev.source)}>{getTitle(ev.source, true)}</RackId>
              <Grow>{ev.message || ev.description || ev.method || '—'}</Grow>
            </Row>
          ))}
        </WBody>
      )}
    </Widget>
  );
};
